<template>
  <div class="z-20 has-footer">
    <DatePicker
      v-model.range="dateValue"
      @update:model-value="onInput"
      ref="datepicker"
      :locale="userLocale"
      :first-day-of-week="2"
      :min-date="minDate"
      :attributes="attributes"
      @popover-did-show="setFooterWidth"
    >
      <template #default="{ togglePopover }">
        <div class="flex z-10" @click="togglePopover">
          <button
            :class="{
              'bg-sky-100 text-sky-800': dateLabel !== '',
              'bg-gray-100 text-gray-900': dateLabel === '',
            }"
            class="whitespace-nowrap flex rounded-sm border-transparent focus:outline-none hover:text-sky-800 hover:bg-sky-100 transition py-2 px-3 sm:text-sm"
          >
            <span class="flex items-center">
              {{
                dateLabel === ""
                  ? props.label
                    ? props.label
                    : t("validation.attributes.schedule_for")
                  : dateLabel
              }}
            </span>
            <span class="ml-2">
              <ChevronDownIcon
                v-if="dateLabel === ''"
                class="h-5 w-5 text-gray-900"
                aria-hidden="true"
              />
              <XMarkIcon
                v-else
                @click.stop.prevent="clear"
                class="h-5 w-5 text-sky-800"
                aria-hidden="true"
              />
            </span>
          </button>
        </div>
      </template>
      <template #footer>
        <div
          ref="footer"
          class="sm:row-start-1 sm:row-end-2 sm:col-start-1 p-4 bg-white flex flex-row sm:flex-col overflow-scroll gap-3 w-full border-t sm:border-t-0 sm:border-r border-black/[.1] rounded-b-lg sm:rounded-b-none sm:rounded-l-lg z-10 max-w-[324px]"
        >
          <span
            v-for="shortcut in customShortcuts"
            :key="'datepicker_shortcut_' + shortcut.label"
            class="text-sm cursor-pointer sm:hover:text-sky-800 sm:hover:bg-transparent bg-gray-100 sm:bg-transparent hover:bg-sky-100 rounded-sm px-4 py-2 sm:p-0 shrink-0 whitespace-nowrap"
            :class="{
              'sm:text-sky-500 bg-sky-100 sm:bg-transparent':
                activeShortcut(shortcut),
            }"
            @click="handleShortcutClick(shortcut)"
            >{{ shortcut.label }}</span
          >
        </div>
      </template>
    </DatePicker>
  </div>
</template>

<script setup>
import { endOfDay, format, isSameDay, startOfDay } from "date-fns";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/vue/20/solid";
import {
  computed,
  defineEmits,
  defineProps,
  nextTick,
  onMounted,
  ref,
  watch,
} from "vue";
import { DatePicker } from "v-calendar";
import { useDatepicker } from "@/Tailwind/Mixins/useDatepicker";

import { useI18n } from "vue-i18n";
import { usePage } from "@inertiajs/vue3";
const { t } = useI18n();

const emit = defineEmits(["change", "shortcut"]);

const props = defineProps({
  value: Object,
  label: String,
  shortcut: String,
  minDate: {
    type: Date,
    default: null,
  },
});

const userLocale = usePage().props.locale ?? "en";

const { customShortcuts } = useDatepicker();

const dateValue = ref({});

const datepicker = ref();

const attributes = [
  {
    key: "today",
    highlight: {
      fillMode: "solid",
      contentClass: "bg-brand-800 text-white current-day-highlight",
    },
    dates: new Date(),
  },
];

const dateLabel = computed(() => {
  if (dateValue.value?.start && dateValue.value?.end) {
    return Object.values(dateValue.value)
      .map((date) => format(new Date(date), "LLL d"))
      .join(" - ");
  }

  return "";
});

const handleShortcutClick = (shortcut) => {
  const value = shortcut.onClick();

  if (value) {
    dateValue.value = value;
    onInput(value);

    datepicker.value.hidePopover();

    // Emit on next tick so the datepicker updates first
    nextTick(() => {
      emit("shortcut", shortcut.key);
    });
  }
};

const activeShortcut = (shortcut) => {
  const value = shortcut.onClick();

  if (!value) {
    return false;
  }

  if (!dateValue.value?.start || !dateValue.value?.end) {
    return false;
  }

  return (
    isSameDay(new Date(dateValue.value.start), value.start) &&
    isSameDay(new Date(dateValue.value.end), value.end)
  );
};

const footer = ref();

onMounted(() => {
  if (!props.value) {
    return;
  }

  if (props.shortcut) {
    const shortcut = customShortcuts.find(
      (shortcut) => shortcut.key === props.shortcut
    );
    handleShortcutClick(shortcut);

    return;
  }

  if (props.value.gte && props.value.lte) {
    const gte = new Date(props.value.gte);
    const lte = new Date(props.value.lte);

    dateValue.value = {
      start: gte,
      end: lte,
    };
  }
});

const setFooterWidth = () => {
  // Root element
  const root = document.querySelector(":root");

  // Get footer width and put it in a css variable
  root.style.setProperty(
    "--datepicker-footer",
    footer.value.offsetWidth + "px"
  );

  document
    .getElementsByClassName("vc-popover-content-wrapper")
    .forEach((element) => {
      // Add class to element
      element.classList.add("show-header");
    });
};

watch(
  () => props.value,
  (value) => {
    if (!value || !value.gte || !value.lte) {
      dateValue.value = {};

      return;
    }

    const gte = new Date(value.gte);
    const lte = new Date(value.lte);

    dateValue.value = {
      start: gte,
      end: lte,
    };
  }
);

const onInput = (value) => {
  // Emit array to comply with previous implementation
  const dates = Object.values(value);
  emit(
    "change",
    value ? [startOfDay(new Date(dates[0])), endOfDay(new Date(dates[1]))] : []
  );

  // Reset shortcut
  emit("shortcut", null);
};

const clear = () => {
  dateValue.value = {};
  emit("change", []);
  datepicker.value.hidePopover();
};
</script>
